import { useState, useRef, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useLanguage } from '@/shared/contexts/LanguageContext';
import { useTemplates, useSaveTemplate } from '@/shared/hooks/useCloudData';
import {
  ContractTemplate, DynamicField, ConsentCheckbox, ContractSection,
  Language, TableCellConfig, TableFieldConfig, CellFieldType
} from '@/shared/types';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowLeft, Plus, Trash2, Type, Mail, Phone, Calendar,
  AlignLeft, List, CheckSquare, Save, ChevronDown,
  Copy, Table, X, MapPin, Loader2, FileText, PenTool,
  Settings2, Camera, Eye, EyeOff,
  User, Lock, Sparkles, GripVertical, Pencil,
} from 'lucide-react';
import { useSectionPresets, SectionPreset, SectionPresetField } from '../hooks/useSectionPresets';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { RichTextEditor } from '@/shared/components/RichTextEditor';
import { RichTextRenderer } from '@/shared/components/RichTextRenderer';
import { useSubscription } from '@/features/subscription/hooks/useSubscription';
import {
  DndContext, closestCenter, PointerSensor, TouchSensor,
  useSensor, useSensors, DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext, useSortable, verticalListSortingStrategy, arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// ─── Types ────────────────────────────────────────────────────────────────────

type BlockType = 'section' | 'field' | 'checkbox';
interface Block { type: BlockType; id: string; }
const emptyML = (): Record<Language, string> => ({ fr: '', en: '', es: '' });
const mkCell = (): TableCellConfig => ({ type: 'text' });
const mkTable = (): TableFieldConfig => ({ columns: [emptyML(), emptyML()], rows: [[mkCell(), mkCell()]] });

// ─── Palette ──────────────────────────────────────────────────────────────────

interface PItem { kind: string; icon: any; label: Record<Language, string>; desc: Record<Language, string>; color: string; }
interface PGroup { id: string; label: Record<Language, string>; icon: any; color: string; items: PItem[]; }

const GROUPS: PGroup[] = [
  {
    id: 'structure', icon: FileText, color: 'text-blue-500',
    label: { fr: 'Structure', en: 'Structure', es: 'Estructura' },
    items: [{ kind: 'section', icon: FileText, color: 'bg-blue-500/10 text-blue-600 border-blue-200/50', label: { fr: 'Clause légale', en: 'Legal clause', es: 'Cláusula' }, desc: { fr: 'Texte riche avec variables', en: 'Rich text with variables', es: 'Texto enriquecido' } }],
  },
  {
    id: 'client', icon: User, color: 'text-emerald-500',
    label: { fr: 'Champs client', en: 'Client fields', es: 'Campos' },
    items: [
      { kind: 'text', icon: Type, color: 'bg-emerald-500/10 text-emerald-600 border-emerald-200/50', label: { fr: 'Texte', en: 'Text', es: 'Texto' }, desc: { fr: 'Réponse courte', en: 'Short answer', es: 'Respuesta corta' } },
      { kind: 'email', icon: Mail, color: 'bg-emerald-500/10 text-emerald-600 border-emerald-200/50', label: { fr: 'Email', en: 'Email', es: 'Email' }, desc: { fr: 'Adresse email', en: 'Email address', es: 'Correo' } },
      { kind: 'tel', icon: Phone, color: 'bg-emerald-500/10 text-emerald-600 border-emerald-200/50', label: { fr: 'Téléphone', en: 'Phone', es: 'Teléfono' }, desc: { fr: 'Numéro de téléphone', en: 'Phone number', es: 'Teléfono' } },
      { kind: 'date', icon: Calendar, color: 'bg-emerald-500/10 text-emerald-600 border-emerald-200/50', label: { fr: 'Date', en: 'Date', es: 'Fecha' }, desc: { fr: 'Sélecteur de date', en: 'Date picker', es: 'Fecha' } },
      { kind: 'address', icon: MapPin, color: 'bg-emerald-500/10 text-emerald-600 border-emerald-200/50', label: { fr: 'Adresse', en: 'Address', es: 'Dirección' }, desc: { fr: 'Adresse complète', en: 'Full address', es: 'Dirección completa' } },
    ],
  },
  {
    id: 'special', icon: Table, color: 'text-violet-500',
    label: { fr: 'Spécial', en: 'Special', es: 'Especial' },
    items: [
      { kind: 'select', icon: List, color: 'bg-violet-500/10 text-violet-600 border-violet-200/50', label: { fr: 'Liste', en: 'Dropdown', es: 'Lista' }, desc: { fr: 'Choix dans une liste', en: 'Choose from list', es: 'Elegir' } },
      { kind: 'table', icon: Table, color: 'bg-violet-500/10 text-violet-600 border-violet-200/50', label: { fr: 'Tableau', en: 'Table', es: 'Tabla' }, desc: { fr: 'Grille de données', en: 'Data grid', es: 'Cuadrícula' } },
      { kind: 'textarea', icon: AlignLeft, color: 'bg-slate-500/10 text-slate-600 border-slate-200/50', label: { fr: 'Texte affiché', en: 'Display text', es: 'Texto' }, desc: { fr: 'Bloc lecture seule', en: 'Read-only block', es: 'Solo lectura' } },
    ],
  },
  {
    id: 'validation', icon: CheckSquare, color: 'text-amber-500',
    label: { fr: 'Validation', en: 'Validation', es: 'Validación' },
    items: [
      { kind: 'checkbox', icon: CheckSquare, color: 'bg-amber-500/10 text-amber-600 border-amber-200/50', label: { fr: 'Case à cocher', en: 'Checkbox', es: 'Casilla' }, desc: { fr: 'Accord ou consentement', en: 'Agreement', es: 'Acuerdo' } },
      { kind: 'signature', icon: PenTool, color: 'bg-purple-500/10 text-purple-600 border-purple-200/50', label: { fr: 'Signature client', en: 'Client signature', es: 'Firma cliente' }, desc: { fr: 'Signature du client', en: 'Client handwritten', es: 'Firma manuscrita' } },
      { kind: 'signature_prat', icon: PenTool, color: 'bg-indigo-500/10 text-indigo-600 border-indigo-200/50', label: { fr: 'Signature praticien', en: 'Practitioner signature', es: 'Firma profesional' }, desc: { fr: 'Signature du tatoueur', en: 'Tattooist signature', es: 'Firma del tatuador' } },
      { kind: 'photo', icon: Camera, color: 'bg-pink-500/10 text-pink-600 border-pink-200/50', label: { fr: 'Photo', en: 'Photo', es: 'Foto' }, desc: { fr: 'Photo du client', en: 'Client photo', es: 'Foto cliente' } },
    ],
  },
];


const CELL_TYPES: { value: CellFieldType; label: Record<Language, string> }[] = [
  { value: 'text', label: { fr: 'Texte', en: 'Text', es: 'Texto' } },
  { value: 'checkbox', label: { fr: 'Case à cocher', en: 'Checkbox', es: 'Casilla' } },
  { value: 'date', label: { fr: 'Date', en: 'Date', es: 'Fecha' } },
  { value: 'select', label: { fr: 'Liste', en: 'Select', es: 'Lista' } },
  { value: 'static-text', label: { fr: 'Texte fixe', en: 'Static text', es: 'Texto fijo' } },
];

function badge(type: BlockType, ft?: DynamicField['type']) {
  if (type === 'section') return { bg: 'bg-blue-500/10 text-blue-700', label: 'Clause', icon: FileText };
  if (type === 'checkbox') return { bg: 'bg-amber-500/10 text-amber-700', label: 'Case', icon: CheckSquare };
  const icons: Record<string, any> = { text: Type, email: Mail, tel: Phone, date: Calendar, textarea: AlignLeft, select: List, table: Table, address: MapPin, signature: PenTool, photo: Camera };
  const colors: Record<string, string> = { text: 'bg-emerald-500/10 text-emerald-700', email: 'bg-emerald-500/10 text-emerald-700', tel: 'bg-emerald-500/10 text-emerald-700', date: 'bg-emerald-500/10 text-emerald-700', address: 'bg-emerald-500/10 text-emerald-700', select: 'bg-violet-500/10 text-violet-700', table: 'bg-violet-500/10 text-violet-700', textarea: 'bg-slate-500/10 text-slate-700', signature: 'bg-purple-500/10 text-purple-700', photo: 'bg-pink-500/10 text-pink-700' };
  return { bg: colors[ft || 'text'] || 'bg-emerald-500/10 text-emerald-700', label: ft || 'Field', icon: icons[ft || 'text'] || Type };
}

// ─── Translation ──────────────────────────────────────────────────────────────

async function translateTexts(texts: string[], src: Language, tgt: Language): Promise<string[]> {
  try {
    const { data, error } = await supabase.functions.invoke('translate', { body: { texts, sourceLang: src, targetLang: tgt } });
    if (error) throw error;
    return data?.translations || texts.map(() => '');
  } catch { return texts.map(() => ''); }
}

function collectTranslatable(tpl: ContractTemplate, src: Language, tgt: Language) {
  const texts: string[] = [], appliers: Array<(t: ContractTemplate, v: string) => ContractTemplate> = [];
  const push = (val: string, fn: (t: ContractTemplate, v: string) => ContractTemplate) => { texts.push(val); appliers.push(fn); };
  if (tpl.name[src] && !tpl.name[tgt]) push(tpl.name[src], (t, v) => ({ ...t, name: { ...t.name, [tgt]: v } }));
  if (tpl.description[src] && !tpl.description[tgt]) push(tpl.description[src], (t, v) => ({ ...t, description: { ...t.description, [tgt]: v } }));
  tpl.sections.forEach((s, si) => {
    if (s.title[src] && !s.title[tgt]) push(s.title[src], (t, v) => { const ss = [...t.sections]; ss[si] = { ...ss[si], title: { ...ss[si].title, [tgt]: v } }; return { ...t, sections: ss }; });
    if (s.content[src] && !s.content[tgt]) push(s.content[src], (t, v) => { const ss = [...t.sections]; ss[si] = { ...ss[si], content: { ...ss[si].content, [tgt]: v } }; return { ...t, sections: ss }; });
  });
  tpl.fields.forEach((f, fi) => { if (f.label[src] && !f.label[tgt]) push(f.label[src], (t, v) => { const ff = [...t.fields]; ff[fi] = { ...ff[fi], label: { ...ff[fi].label, [tgt]: v } }; return { ...t, fields: ff }; }); });
  tpl.checkboxes.forEach((c, ci) => { if (c.label[src] && !c.label[tgt]) push(c.label[src], (t, v) => { const cc = [...t.checkboxes]; cc[ci] = { ...cc[ci], label: { ...cc[ci].label, [tgt]: v } }; return { ...t, checkboxes: cc }; }); });
  const apply = (translations: string[], t: ContractTemplate): ContractTemplate => { let r = t; translations.forEach((v, i) => { if (v && appliers[i]) r = appliers[i](r, v); }); return r; };
  return { texts, apply };
}

// ─── Table sub-components ─────────────────────────────────────────────────────

function CellEditor({ cell, lang, onChange, rawSel, onRawChange, onRawBlur }: { cell: TableCellConfig; lang: Language; onChange: (u: TableCellConfig) => void; rawSel: string; onRawChange: (s: string) => void; onRawBlur: () => void }) {
  return (
    <div className="space-y-1.5 p-2 min-w-[140px]">
      <select value={cell.type} onChange={e => { const t = e.target.value as CellFieldType; const u: TableCellConfig = { type: t }; if (t === 'checkbox' || t === 'static-text') u.label = cell.label || emptyML(); if (t === 'select') u.options = cell.options || { fr: [], en: [], es: [] }; onChange(u); }} className="w-full p-1.5 bg-background border border-border rounded-lg text-xs focus:border-primary focus:outline-none">
        {CELL_TYPES.map(ct => <option key={ct.value} value={ct.value}>{ct.label[lang]}</option>)}
      </select>
      {(cell.type === 'checkbox' || cell.type === 'static-text') && <input value={cell.label?.[lang] || ''} onChange={e => onChange({ ...cell, label: { ...(cell.label || emptyML()), [lang]: e.target.value } })} placeholder={cell.type === 'checkbox' ? 'Libellé' : 'Texte'} className="w-full p-1.5 bg-background border border-border rounded-lg text-xs focus:border-primary focus:outline-none" />}
      {cell.type === 'select' && <div className="space-y-1"><input value={rawSel} onChange={e => onRawChange(e.target.value)} onBlur={onRawBlur} placeholder="Opt1, Opt2" className="w-full p-1.5 bg-background border border-border rounded-lg text-xs focus:border-primary focus:outline-none" /><p className="text-[10px] text-muted-foreground">Séparer par virgules</p></div>}
    </div>
  );
}

function TableEditor({ field, lang, onUpdate }: { field: DynamicField; lang: Language; onUpdate: (p: Partial<DynamicField>) => void }) {
  const cfg = field.tableConfig || mkTable();
  const [raw, setRaw] = useState<Record<string, string>>({});
  const set = (c: TableFieldConfig) => onUpdate({ tableConfig: c });
  const getRaw = (ri: number, ci: number) => { const k = `${ri}-${ci}`; return raw[k] !== undefined ? raw[k] : (cfg.rows[ri]?.[ci]?.type === 'select' ? (cfg.rows[ri][ci].options?.[lang] || []).join(', ') : ''); };
  const updateCell = (ri: number, ci: number, cell: TableCellConfig) => { const rows = cfg.rows.map((r, ri2) => ri2 === ri ? r.map((c, ci2) => ci2 === ci ? cell : c) : [...r]); set({ ...cfg, rows }); };
  const flush = (ri: number, ci: number) => { const opts = getRaw(ri, ci).split(',').map(s => s.trim()).filter(Boolean); const cell = cfg.rows[ri]?.[ci]; if (!cell || cell.type !== 'select') return; const o: Record<Language, string[]> = { fr: cell.options?.fr || [], en: cell.options?.en || [], es: cell.options?.es || [] }; o[lang] = opts; updateCell(ri, ci, { ...cell, options: o }); };
  return (
    <div className="mt-3 overflow-x-auto rounded-xl border border-border">
      <table className="border-collapse w-full">
        <thead>
          <tr className="bg-muted/50">
            <th className="p-1 border-b border-r border-border w-7 text-xs text-muted-foreground">#</th>
            {cfg.columns.map((col, ci) => (
              <th key={ci} className="border-b border-r border-border p-1.5 min-w-[130px]">
                <div className="flex items-center gap-1">
                  <input value={col[lang] || ''} onChange={e => { const cols = cfg.columns.map((c, i) => i === ci ? { ...c, [lang]: e.target.value } : c); set({ ...cfg, columns: cols }); }} placeholder={`Col ${ci + 1}`} className="flex-1 p-1 bg-background border border-border rounded text-xs font-semibold focus:border-primary focus:outline-none" />
                  <button onClick={() => { if (cfg.columns.length > 1) set({ columns: cfg.columns.filter((_, i) => i !== ci), rows: cfg.rows.map(r => r.filter((_, i) => i !== ci)) }); }} className="p-0.5 text-destructive hover:bg-destructive/10 rounded"><X size={11} /></button>
                </div>
              </th>
            ))}
            <th className="border-b border-border p-1 w-7"><button onClick={() => set({ columns: [...cfg.columns, emptyML()], rows: cfg.rows.map(r => [...r, mkCell()]) })} className="p-1 text-primary hover:bg-primary/10 rounded"><Plus size={13} /></button></th>
          </tr>
        </thead>
        <tbody>
          {cfg.rows.map((row, ri) => (
            <tr key={ri} className={ri % 2 === 0 ? '' : 'bg-muted/20'}>
              <td className="border-b border-r border-border p-1 text-center"><button onClick={() => { if (cfg.rows.length > 1) set({ ...cfg, rows: cfg.rows.filter((_, i) => i !== ri) }); }} className="p-0.5 text-destructive hover:bg-destructive/10 rounded"><X size={11} /></button></td>
              {row.map((cell, ci) => <td key={ci} className="border-b border-r border-border align-top"><CellEditor cell={cell} lang={lang} onChange={u => updateCell(ri, ci, u)} rawSel={getRaw(ri, ci)} onRawChange={s => setRaw(p => ({ ...p, [`${ri}-${ci}`]: s }))} onRawBlur={() => flush(ri, ci)} /></td>)}
              <td className="border-b border-border" />
            </tr>
          ))}
        </tbody>
        <tfoot><tr><td colSpan={cfg.columns.length + 2} className="p-2"><button onClick={() => set({ ...cfg, rows: [...cfg.rows, cfg.columns.map(() => mkCell())] })} className="flex items-center gap-1 text-xs text-primary font-medium hover:bg-primary/10 px-2.5 py-1.5 rounded-lg"><Plus size={13} /> Ajouter une ligne</button></td></tr></tfoot>
      </table>
    </div>
  );
}

// ─── Preview ──────────────────────────────────────────────────────────────────

function Preview({ tpl, lang }: { tpl: ContractTemplate; lang: Language }) {
  const hasContent = tpl.sections.length > 0 || tpl.fields.length > 0 || tpl.checkboxes.length > 0;
  if (!hasContent) return <div className="flex flex-col items-center justify-center h-48 text-center text-muted-foreground"><Eye size={36} className="opacity-15 mb-2" /><p className="text-xs">L'aperçu apparaît ici</p></div>;
  return (
    <div className="space-y-3.5 text-sm">
      {tpl.name[lang] && <div className="rounded-2xl bg-primary/5 border border-primary/10 p-3.5"><p className="font-bold text-sm">{tpl.name[lang]}</p>{tpl.description[lang] && <p className="text-xs text-muted-foreground mt-0.5">{tpl.description[lang]}</p>}</div>}
      {tpl.sections.map(s => (
        <div key={s.id} className="space-y-2">
          {s.title[lang] && <div className="flex items-center gap-2"><div className="h-px flex-1 bg-border/40" /><span className="text-[10px] font-bold text-primary/60 uppercase tracking-wider px-1">{s.title[lang]}</span><div className="h-px flex-1 bg-border/40" /></div>}
          {s.content[lang] && <RichTextRenderer html={s.content[lang]} className="rounded-xl bg-muted/30 p-3 text-xs text-foreground/80 leading-relaxed" />}
        </div>
      ))}
      {tpl.fields.map(f => {
        if (f.type === 'textarea') return <div key={f.id}>{f.label[lang] && <p className="text-xs font-semibold mb-1">{f.label[lang]}</p>}<RichTextRenderer html={f.placeholder?.[lang] || ''} className="rounded-xl bg-muted/30 p-3 text-xs text-foreground/70" /></div>;
        if (f.type === 'signature') return <div key={f.id}><p className="text-xs font-semibold mb-1">{f.label[lang] || 'Signature'}{f.required && ' *'}</p><div className="h-12 rounded-xl border-2 border-dashed border-border/40 flex items-center justify-center"><PenTool size={14} className="text-muted-foreground/30" /></div></div>;
        if (f.type === 'photo') return <div key={f.id}><p className="text-xs font-semibold mb-1">{f.label[lang] || 'Photo'}{f.required && ' *'}</p><div className="h-12 rounded-xl border-2 border-dashed border-border/40 flex items-center justify-center"><Camera size={14} className="text-muted-foreground/30" /></div></div>;
        if (f.type === 'address') return <div key={f.id}><p className="text-xs font-semibold mb-1">{f.label[lang] || 'Adresse'}{f.required && ' *'}</p><div className="grid grid-cols-2 gap-1">{['Rue','Ville','CP','Pays'].map(l => <div key={l} className="h-6 rounded-lg bg-muted/30 border border-border/30 px-2 flex items-center"><span className="text-[10px] text-muted-foreground/40">{l}</span></div>)}</div></div>;
        if (f.type === 'table' && f.tableConfig) return (
          <div key={f.id}>{f.label[lang] && <p className="text-xs font-semibold mb-1">{f.label[lang]}</p>}
            <div className="overflow-x-auto rounded-xl border border-border/40"><table className="w-full text-xs border-collapse"><thead className="bg-primary/5"><tr>{f.tableConfig.columns.map((c, ci) => <th key={ci} className="p-1.5 text-left font-medium border-b border-border/30">{c[lang] || `Col ${ci+1}`}</th>)}</tr></thead><tbody>{f.tableConfig.rows.map((row, ri) => <tr key={ri} className={ri%2===0?'':'bg-muted/20'}>{row.map((cell, ci) => <td key={ci} className="p-1.5 border-b border-border/20">{cell.type==='checkbox'?<div className="flex items-center gap-1"><div className="w-3 h-3 rounded border border-border/50"/><span className="text-[10px] text-muted-foreground">{cell.label?.[lang]||''}</span></div>:cell.type==='static-text'?<span className="text-[10px] text-muted-foreground italic">{cell.label?.[lang]||''}</span>:<div className="h-4 rounded bg-border/20"/>}</td>)}</tr>)}</tbody></table></div>
          </div>
        );
        return <div key={f.id}><p className="text-xs font-semibold mb-1">{f.label[lang] || <span className="italic text-muted-foreground text-[10px]">Sans label</span>}{f.required && <span className="text-destructive ml-1">*</span>}</p>{f.type==='select'?<div className="h-7 rounded-xl bg-muted/30 border border-border/30 px-2.5 flex items-center justify-between"><span className="text-[10px] text-muted-foreground/40">{f.options?.[lang]?.[0]||'...'}</span><ChevronDown size={10} className="text-muted-foreground/30"/></div>:<div className="h-7 rounded-xl bg-muted/30 border border-border/30"/>}</div>;
      })}
      {tpl.checkboxes.map(cb => <div key={cb.id} className="flex items-start gap-2"><div className="w-3.5 h-3.5 rounded border border-border/50 mt-0.5 shrink-0"/><RichTextRenderer html={cb.label[lang] || '<em class="text-muted-foreground">Sans texte</em>'} className="text-xs text-foreground/80 leading-relaxed" />{cb.required && <span className="text-destructive text-[10px] shrink-0">*</span>}</div>)}
    </div>
  );
}

// ─── Drag & Drop sortable list ────────────────────────────────────────────────

interface SortableBlockProps {
  block: Block;
  expanded: boolean;
  onToggle: () => void;
  onRemove: () => void;
  onDuplicate: () => void;
  label: string;
  fieldType?: DynamicField['type'];
  children: React.ReactNode;
}

function SortableBlock({ block, expanded, onToggle, onRemove, onDuplicate, label, fieldType, children }: SortableBlockProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: block.id });
  const b = badge(block.type, fieldType);
  const BIcon = b.icon;
  const style = { transform: CSS.Transform.toString(transform), transition };
  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`glass-card overflow-hidden transition-all duration-150 ${isDragging ? 'opacity-40 z-50' : ''}`}
    >
      <div className="flex items-center gap-2 px-3 py-2.5">
        <div {...attributes} {...listeners}
          className="cursor-grab active:cursor-grabbing p-1 rounded hover:bg-secondary text-muted-foreground shrink-0 touch-none">
          <GripVertical size={15} />
        </div>
        <span className={`flex items-center gap-1 px-2 py-0.5 rounded-lg text-[10px] font-bold uppercase tracking-wide shrink-0 ${b.bg}`}>
          <BIcon size={10} />{block.type === 'section' ? 'Clause' : block.type === 'checkbox' ? 'Case' : fieldType || 'Champ'}
        </span>
        <span className="flex-1 text-sm font-medium text-foreground truncate cursor-pointer" onClick={onToggle}>{label}</span>
        <div className="flex items-center gap-0.5 shrink-0">
          <button onClick={onDuplicate} title="Dupliquer" className="p-1.5 text-muted-foreground hover:text-primary hover:bg-primary/10 rounded-lg transition-colors"><Copy size={13} /></button>
          <button onClick={onRemove} title="Supprimer" className="p-1.5 text-muted-foreground hover:text-destructive hover:bg-destructive/10 rounded-lg transition-colors"><Trash2 size={13} /></button>
          <button onClick={onToggle} className="p-1.5 text-muted-foreground rounded-lg"><ChevronDown size={14} className={`transition-transform ${expanded ? 'rotate-180' : ''}`} /></button>
        </div>
      </div>
      <AnimatePresence>
        {expanded && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
            <div className="px-4 pb-4 pt-3 border-t border-border/50">{children}</div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ─── Main Editor ──────────────────────────────────────────────────────────────

const TemplateEditor = () => {
  const { templateId } = useParams<{ templateId: string }>();
  const navigate = useNavigate();
  const { language, t } = useLanguage();
  const isNew = templateId === 'new';
  const { data: cloudTemplates = [], isPending: isLoadingTemplates } = useTemplates();
  const saveMutation = useSaveTemplate();
  const { tier, isInTrial } = useSubscription();

  const [tpl, setTpl] = useState<ContractTemplate>(() => {
    if (!isNew) { const found = cloudTemplates.find(t => t.id === templateId); if (found) return { ...found }; }
    return { id: crypto.randomUUID(), name: emptyML(), category: 'tattoo', description: emptyML(), icon: '📄', sections: [], fields: [], checkboxes: [], tags: [], archived: false, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() };
  });

  const [blocks, setBlocks] = useState<Block[]>(() => {
    const b: Block[] = [];
    tpl.sections.forEach(s => b.push({ type: 'section', id: s.id }));
    tpl.fields.forEach(f => b.push({ type: 'field', id: f.id }));
    tpl.checkboxes.forEach(c => b.push({ type: 'checkbox', id: c.id }));
    return b;
  });

  const [loadedId, setLoadedId] = useState<string | null>(null);
  if (!isNew && cloudTemplates.length > 0 && loadedId !== templateId) {
    const found = cloudTemplates.find(t => t.id === templateId);
    if (found) {
      setTpl({ ...found });
      const b: Block[] = [];
      found.sections.forEach(s => b.push({ type: 'section', id: s.id }));
      found.fields.forEach(f => b.push({ type: 'field', id: f.id }));
      found.checkboxes.forEach(c => b.push({ type: 'checkbox', id: c.id }));
      setBlocks(b);
      setLoadedId(templateId!);
    }
  }

  useEffect(() => {
    if (isNew || isLoadingTemplates) return;
    const found = cloudTemplates.find(t => t.id === templateId);
    if (!found) navigate('/', { replace: true });
  }, [isLoadingTemplates, cloudTemplates, templateId, isNew, navigate]);

  const { presets, createPreset, updatePreset, deletePreset } = useSectionPresets();

  const [expanded, setExpanded] = useState<string | null>(null);
  const [showInfo, setShowInfo] = useState(true);
  const [showPreview, setShowPreview] = useState(true);
  const [isTranslating, setIsTranslating] = useState(false);
  const [openGroup, setOpenGroup] = useState<string | null>(null);
  const [sectionAddMenu, setSectionAddMenu] = useState<string | null>(null);

  // Preset panel state
  type PanelMode = 'create' | 'edit';
  const [panelOpen, setPanelOpen] = useState(false);
  const [panelMode, setPanelMode] = useState<PanelMode>('create');
  const [editingPreset, setEditingPreset] = useState<SectionPreset | null>(null);
  const [panelName, setPanelName] = useState('');
  const [panelFields, setPanelFields] = useState<SectionPresetField[]>([]);
  const [panelAddMenu, setPanelAddMenu] = useState(false);
  const [deleteDialogPreset, setDeleteDialogPreset] = useState<SectionPreset | null>(null);

  const openCreatePanel = () => {
    setPanelMode('create');
    setEditingPreset(null);
    setPanelName('');
    setPanelFields([]);
    setPanelOpen(true);
  };
  const openEditPanel = (preset: SectionPreset) => {
    setPanelMode('edit');
    setEditingPreset(preset);
    setPanelName(preset.name);
    setPanelFields(preset.fields.map(f => ({ ...f })));
    setPanelOpen(true);
  };
  const closePanel = () => { setPanelOpen(false); setPanelAddMenu(false); };
  const savePanel = async () => {
    if (!panelName.trim()) return;
    if (panelMode === 'create') {
      await createPreset.mutateAsync({ name: panelName.trim(), fields: panelFields });
    } else if (editingPreset) {
      await updatePreset.mutateAsync({ id: editingPreset.id, name: panelName.trim(), fields: panelFields });
    }
    closePanel();
  };
  const addPanelField = (kind: string) => {
    const f: SectionPresetField = {
      id: crypto.randomUUID(),
      key: kind === 'signature' ? 'SIGNATURE' : `FIELD_${Date.now()}`,
      label: { fr: '', en: '', es: '' },
      type: kind as SectionPresetField['type'],
      required: kind === 'signature',
    };
    setPanelFields(p => [...p, f]);
    setPanelAddMenu(false);
  };
  const removePanelField = (id: string) => setPanelFields(p => p.filter(f => f.id !== id));
  const updatePanelFieldLabel = (id: string, val: string) =>
    setPanelFields(p => p.map(f => f.id === id ? { ...f, label: { ...f.label, fr: val } } : f));

  const prevLang = useRef<Language>(language);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 5 } }),
  );

  useEffect(() => {
    const prev = prevLang.current;
    if (prev === language) return;
    prevLang.current = language;
    const { texts, apply } = collectTranslatable(tpl, prev, language);
    if (!texts.length) return;
    setIsTranslating(true);
    translateTexts(texts, prev, language).then(res => { setTpl(p => apply(res, p)); setIsTranslating(false); toast.success(`${res.filter(Boolean).length} textes traduits`); }).catch(() => setIsTranslating(false));
  }, [language]);

  const upd = (p: Partial<ContractTemplate>) => setTpl(prev => ({ ...prev, ...p }));
  const updField = (id: string, p: Partial<DynamicField>) => upd({ fields: tpl.fields.map(f => f.id === id ? { ...f, ...p } : f) });
  const updSec = (id: string, p: Partial<ContractSection>) => upd({ sections: tpl.sections.map(s => s.id === id ? { ...s, ...p } : s) });
  const updCb = (id: string, p: Partial<ConsentCheckbox>) => upd({ checkboxes: tpl.checkboxes.map(c => c.id === id ? { ...c, ...p } : c) });

  const syncOrder = (reordered: Block[]) => {
    upd({
      sections: reordered.filter(b => b.type === 'section').map(b => tpl.sections.find(s => s.id === b.id)!).filter(Boolean),
      fields: reordered.filter(b => b.type === 'field').map(b => tpl.fields.find(f => f.id === b.id)!).filter(Boolean),
      checkboxes: reordered.filter(b => b.type === 'checkbox').map(b => tpl.checkboxes.find(c => c.id === b.id)!).filter(Boolean),
    });
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIdx = blocks.findIndex(b => b.id === active.id);
    const newIdx = blocks.findIndex(b => b.id === over.id);
    const reordered = arrayMove(blocks, oldIdx, newIdx);
    setBlocks(reordered);
    syncOrder(reordered);
  };

  const addEl = (kind: string) => {
    // Don't close the group when adding
    if (kind === 'section') {
      const s: ContractSection = { id: crypto.randomUUID(), title: emptyML(), content: emptyML() };
      upd({ sections: [...tpl.sections, s] });
      setBlocks(p => [...p, { type: 'section', id: s.id }]);
      setExpanded(s.id);
    } else if (kind === 'checkbox') {
      const cb: ConsentCheckbox = { id: crypto.randomUUID(), label: emptyML(), required: false, defaultChecked: false };
      upd({ checkboxes: [...tpl.checkboxes, cb] });
      setBlocks(p => [...p, { type: 'checkbox', id: cb.id }]);
      setExpanded(cb.id);
    } else {
      const isPrat = kind === 'signature_prat';
      const actualKind = isPrat ? 'signature' : kind;
      const f: DynamicField = {
        id: crypto.randomUUID(),
        key: isPrat ? 'SIGNATURE_PRATICIEN' : kind === 'signature' ? 'SIGNATURE' : kind === 'photo' ? 'PHOTO_CLIENT' : `FIELD_${Date.now()}`,
        label: isPrat ? { fr: 'Signature du praticien', en: 'Practitioner signature', es: 'Firma del profesional' } : kind === 'signature' ? { fr: 'Signature du client', en: 'Client signature', es: 'Firma del cliente' } : kind === 'photo' ? { fr: 'Photo du client', en: 'Client photo', es: 'Foto del cliente' } : emptyML(),
        type: actualKind as DynamicField['type'],
        required: kind === 'signature',
        ...(kind === 'select' ? { options: { fr: ['Option 1'], en: ['Option 1'], es: ['Opción 1'] } } : {}),
        ...(kind === 'table' ? { tableConfig: mkTable() } : {}),
        ...(kind === 'address' ? { addressConfig: { defaultCountry: 'France', showDepartment: false } } : {}),
        ...(kind === 'textarea' ? { placeholder: emptyML() } : {}),
      };
      upd({ fields: [...tpl.fields, f] });
      setBlocks(p => [...p, { type: 'field', id: f.id }]);
      setExpanded(f.id);
    }
  };

  const addFieldToSection = (sectionId: string, kind: string) => {
    const sectionBlockIdx = blocks.findIndex(b => b.id === sectionId);
    if (sectionBlockIdx === -1) return;

    if (kind === 'checkbox') {
      const cb: ConsentCheckbox = { id: crypto.randomUUID(), label: emptyML(), required: false, defaultChecked: false };
      upd({ checkboxes: [...tpl.checkboxes, cb] });
      const newBlocks = [...blocks];
      newBlocks.splice(sectionBlockIdx + 1, 0, { type: 'checkbox', id: cb.id });
      setBlocks(newBlocks);
      setExpanded(cb.id);
    } else {
      const f: DynamicField = {
        id: crypto.randomUUID(),
        key: kind === 'signature' ? 'SIGNATURE' : `FIELD_${Date.now()}`,
        label: kind === 'signature' ? { fr: 'Signature du client', en: 'Client signature', es: 'Firma del cliente' } : emptyML(),
        type: kind as DynamicField['type'],
        required: kind === 'signature',
        sectionId,
        ...(kind === 'address' ? { addressConfig: { defaultCountry: 'France', showDepartment: false } } : {}),
      };
      upd({ fields: [...tpl.fields, f] });
      const newBlocks = [...blocks];
      newBlocks.splice(sectionBlockIdx + 1, 0, { type: 'field', id: f.id });
      setBlocks(newBlocks);
      setExpanded(f.id);
    }
  };

  const addPresetFromLibrary = (preset: SectionPreset) => {
    const newFields: DynamicField[] = preset.fields.map(f => ({
      id: crypto.randomUUID(),
      key: f.key,
      label: { ...f.label },
      type: f.type,
      required: f.required,
      ...(f.type === 'address' ? { addressConfig: { defaultCountry: 'France', showDepartment: false } } : {}),
    }));
    const newBlocks: Block[] = newFields.map(f => ({ type: 'field' as BlockType, id: f.id }));
    upd({ fields: [...tpl.fields, ...newFields] });
    setBlocks(p => [...p, ...newBlocks]);
    if (newBlocks.length > 0) setExpanded(newBlocks[0].id);
  };

  const removeBlock = (block: Block) => {
    setBlocks(p => p.filter(b => b.id !== block.id));
    if (block.type === 'section') upd({ sections: tpl.sections.filter(s => s.id !== block.id) });
    else if (block.type === 'field') upd({ fields: tpl.fields.filter(f => f.id !== block.id) });
    else upd({ checkboxes: tpl.checkboxes.filter(c => c.id !== block.id) });
    if (expanded === block.id) setExpanded(null);
  };

  const duplicateBlock = (block: Block) => {
    const newId = crypto.randomUUID();
    if (block.type === 'section') {
      const orig = tpl.sections.find(s => s.id === block.id)!;
      const copy: ContractSection = { ...orig, id: newId, title: { ...orig.title } };
      const idx = tpl.sections.indexOf(orig);
      const newSections = [...tpl.sections];
      newSections.splice(idx + 1, 0, copy);
      upd({ sections: newSections });
    } else if (block.type === 'field') {
      const orig = tpl.fields.find(f => f.id === block.id)!;
      const copy: DynamicField = { ...orig, id: newId, key: `${orig.key}_COPY` };
      const idx = tpl.fields.indexOf(orig);
      const newFields = [...tpl.fields];
      newFields.splice(idx + 1, 0, copy);
      upd({ fields: newFields });
    } else {
      const orig = tpl.checkboxes.find(c => c.id === block.id)!;
      const copy: ConsentCheckbox = { ...orig, id: newId };
      const idx = tpl.checkboxes.indexOf(orig);
      const newCbs = [...tpl.checkboxes];
      newCbs.splice(idx + 1, 0, copy);
      upd({ checkboxes: newCbs });
    }
    const blockIdx = blocks.findIndex(b => b.id === block.id);
    const newBlocks = [...blocks];
    newBlocks.splice(blockIdx + 1, 0, { type: block.type, id: newId });
    setBlocks(newBlocks);
    setExpanded(newId);
  };

  const addSelectOpt = (fid: string) => { const f = tpl.fields.find(f => f.id === fid); if (!f?.options) return; updField(fid, { options: { fr: [...(f.options.fr || []), ''], en: [...(f.options.en || []), ''], es: [...(f.options.es || []), ''] } }); };
  const updSelOpt = (fid: string, oi: number, v: string) => { const f = tpl.fields.find(f => f.id === fid); if (!f?.options) return; const arr = [...(f.options[language] || [])]; arr[oi] = v; updField(fid, { options: { ...f.options, [language]: arr } }); };
  const rmSelOpt = (fid: string, oi: number) => { const f = tpl.fields.find(f => f.id === fid); if (!f?.options) return; updField(fid, { options: { fr: f.options.fr.filter((_, i) => i !== oi), en: f.options.en.filter((_, i) => i !== oi), es: f.options.es.filter((_, i) => i !== oi) } }); };

  const getLabel = (b: Block) => {
    if (b.type === 'section') return tpl.sections.find(s => s.id === b.id)?.title[language] || 'Clause sans titre';
    if (b.type === 'checkbox') return tpl.checkboxes.find(c => c.id === b.id)?.label[language]?.replace(/<[^>]+>/g, '').slice(0, 60) || 'Case à cocher';
    return tpl.fields.find(f => f.id === b.id)?.label[language] || 'Champ sans label';
  };
  const getFT = (b: Block): DynamicField['type'] | undefined => b.type !== 'field' ? undefined : tpl.fields.find(f => f.id === b.id)?.type;

  const renderContent = (b: Block) => {
    if (b.type === 'section') {
      const s = tpl.sections.find(s => s.id === b.id); if (!s) return null;
      return (
        <div className="space-y-4">
          <div className="space-y-1.5">
            <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Titre de la clause</label>
            <input value={s.title[language]} onChange={e => updSec(s.id, { title: { ...s.title, [language]: e.target.value } })} placeholder="Ex: Consentement éclairé..." className="w-full p-3 bg-background border-2 border-border rounded-xl text-sm focus:border-primary focus:outline-none" />
          </div>
          <div className="space-y-1.5">
            <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Texte légal</label>
            <RichTextEditor value={s.content[language] || ''} onChange={html => updSec(s.id, { content: { ...s.content, [language]: html } })} placeholder="Rédigez le texte légal..." minHeight={160} customVars={tpl.fields.filter(f => !f.key.startsWith('FIELD_')).map(f => ({ key: f.key, label: f.label[language] || f.key }))} />
          </div>
          <div className="relative">
            <button
              onClick={() => setSectionAddMenu(sectionAddMenu === s.id ? null : s.id)}
              className="flex items-center gap-1.5 text-xs text-primary font-medium px-3 py-1.5 rounded-lg border border-primary/20 hover:bg-primary/5 transition-colors"
            >
              <Plus size={12} /> Ajouter un champ
            </button>
            {sectionAddMenu === s.id && (
              <>
              <div className="fixed inset-0 z-10" onClick={() => setSectionAddMenu(null)} />
              <div className="absolute top-full left-0 mt-1 z-20 bg-card border border-border rounded-xl shadow-lg p-2 flex flex-wrap gap-1 min-w-[260px]">
                {[
                  { kind: 'text', label: 'Texte court', icon: Type },
                  { kind: 'email', label: 'Email', icon: Mail },
                  { kind: 'tel', label: 'Téléphone', icon: Phone },
                  { kind: 'date', label: 'Date', icon: Calendar },
                  { kind: 'address', label: 'Adresse', icon: MapPin },
                  { kind: 'checkbox', label: 'Case à cocher', icon: CheckSquare },
                  { kind: 'signature', label: 'Signature', icon: PenTool },
                ].map(({ kind, label, icon: Icon }) => (
                  <button
                    key={kind}
                    onClick={() => { addFieldToSection(s.id, kind); setSectionAddMenu(null); }}
                    className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg hover:bg-secondary text-xs font-medium transition-colors"
                  >
                    <Icon size={11} /> {label}
                  </button>
                ))}
              </div>
              </>
            )}
          </div>
        </div>
      );
    }
    if (b.type === 'checkbox') {
      const cb = tpl.checkboxes.find(c => c.id === b.id); if (!cb) return null;
      return (
        <div className="space-y-4">
          <div className="flex gap-4">
            <label className="flex items-center gap-2 text-sm cursor-pointer select-none"><input type="checkbox" checked={cb.required} onChange={e => updCb(cb.id, { required: e.target.checked })} className="w-4 h-4 rounded accent-primary" /><span>Obligatoire</span></label>
            <label className="flex items-center gap-2 text-sm cursor-pointer select-none"><input type="checkbox" checked={cb.defaultChecked || false} onChange={e => updCb(cb.id, { defaultChecked: e.target.checked })} className="w-4 h-4 rounded accent-primary" /><span>Coché par défaut</span></label>
          </div>
          <div className="space-y-1.5">
            <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Texte du consentement</label>
            <RichTextEditor value={cb.label[language] || ''} onChange={html => updCb(cb.id, { label: { ...cb.label, [language]: html } })} placeholder="Ex: J'accepte les conditions..." minHeight={100} />
          </div>
        </div>
      );
    }
    const f = tpl.fields.find(f => f.id === b.id); if (!f) return null;
    if (f.type === 'signature' || f.type === 'photo') return (
      <div className="space-y-3">
        <div className="space-y-1.5">
          <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Label</label>
          <input value={f.label[language]} onChange={e => updField(f.id, { label: { ...f.label, [language]: e.target.value } })} placeholder="Libellé affiché" className="w-full p-2.5 bg-background border-2 border-border rounded-xl text-sm focus:border-primary focus:outline-none" />
        </div>
        <label className="flex items-center gap-2 text-sm cursor-pointer select-none"><input type="checkbox" checked={f.required} onChange={e => updField(f.id, { required: e.target.checked })} className="w-4 h-4 rounded accent-primary" /><span>Obligatoire</span></label>
        <div className="flex items-start gap-2 p-3 rounded-xl bg-primary/5 border border-primary/10">
          <Sparkles size={13} className="text-primary mt-0.5 shrink-0" />
          <p className="text-xs text-muted-foreground">{f.type === 'signature' ? (f.key === 'SIGNATURE_PRATICIEN' ? 'Le praticien signe ici après vérification du consentement' : 'Le client signera avec son doigt ou un stylet') : 'Le client peut prendre une photo ou en importer une'}</p>
        </div>
      </div>
    );
    if (f.type === 'textarea') return (
      <div className="space-y-4">
        <div className="space-y-1.5">
          <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Titre (optionnel)</label>
          <input value={f.label[language]} onChange={e => updField(f.id, { label: { ...f.label, [language]: e.target.value } })} placeholder="Titre du bloc" className="w-full p-2.5 bg-background border-2 border-border rounded-xl text-sm focus:border-primary focus:outline-none" />
        </div>
        <div className="space-y-1.5">
          <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Contenu (lecture seule)</label>
          <RichTextEditor value={f.placeholder?.[language] || ''} onChange={html => updField(f.id, { placeholder: { ...(f.placeholder || emptyML()), [language]: html } })} placeholder="Texte affiché en lecture seule..." minHeight={140} customVars={tpl.fields.filter(f => !f.key.startsWith('FIELD_')).map(f => ({ key: f.key, label: f.label[language] || f.key }))} />
        </div>
      </div>
    );
    return (
      <div className="space-y-4">
        <div className="space-y-1.5">
          <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Label</label>
          <input value={f.label[language]} onChange={e => {
            const v = e.target.value;
            const updates: Partial<DynamicField> = { label: { ...f.label, [language]: v } };
            if (f.key.startsWith('FIELD_') && v) updates.key = v.toUpperCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^A-Z0-9]/g, '_').replace(/_+/g, '_').slice(0, 30);
            updField(f.id, updates);
          }} placeholder="Label visible par le client" className="w-full p-2.5 bg-background border-2 border-border rounded-xl text-sm focus:border-primary focus:outline-none" />
        </div>
        <label className="flex items-center gap-2 text-sm cursor-pointer select-none"><input type="checkbox" checked={f.required} onChange={e => updField(f.id, { required: e.target.checked })} className="w-4 h-4 rounded accent-primary" /><span>Obligatoire</span></label>
        {f.type === 'select' && (
          <div className="space-y-2 pl-4 border-l-2 border-primary/20">
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold text-muted-foreground">Options</span>
              <button onClick={() => addSelectOpt(f.id)} className="px-2 py-1 bg-primary/10 text-primary rounded-lg text-xs font-medium flex items-center gap-1 hover:bg-primary/20"><Plus size={11} /> Ajouter</button>
            </div>
            {(f.options?.[language] || []).map((opt, oi) => <div key={oi} className="flex items-center gap-2"><span className="text-xs text-muted-foreground w-4 text-right">{oi + 1}</span><input value={opt} onChange={e => updSelOpt(f.id, oi, e.target.value)} placeholder={`Option ${oi + 1}`} className="flex-1 p-2 bg-background border border-border rounded-lg text-sm focus:border-primary focus:outline-none" /><button onClick={() => rmSelOpt(f.id, oi)} className="p-1 text-destructive hover:bg-destructive/10 rounded"><Trash2 size={12} /></button></div>)}
          </div>
        )}
        {f.type === 'table' && <TableEditor field={f} lang={language} onUpdate={p => updField(f.id, p)} />}
        {f.type === 'address' && (
          <div className="space-y-2 pl-4 border-l-2 border-primary/20">
            <div className="flex items-center gap-3"><span className="text-xs text-muted-foreground">Pays par défaut</span><select value={f.addressConfig?.defaultCountry || 'France'} onChange={e => updField(f.id, { addressConfig: { ...(f.addressConfig || { defaultCountry: 'France', showDepartment: false }), defaultCountry: e.target.value } })} className="flex-1 p-2 bg-background border border-border rounded-lg text-sm focus:border-primary focus:outline-none">{['France','Belgique','Suisse','Canada','Luxembourg','Espagne','Italie','Allemagne','Portugal','Royaume-Uni','États-Unis','Maroc','Tunisie','Algérie'].map(c => <option key={c} value={c}>{c}</option>)}</select></div>
            <label className="flex items-center gap-2 text-sm cursor-pointer select-none"><input type="checkbox" checked={f.addressConfig?.showDepartment || false} onChange={e => updField(f.id, { addressConfig: { ...(f.addressConfig || { defaultCountry: 'France', showDepartment: false }), showDepartment: e.target.checked } })} className="w-4 h-4 rounded accent-primary" />Afficher Département / Région</label>
          </div>
        )}
      </div>
    );
  };

  const handleSave = () => {
    if (!tpl.name.fr && !tpl.name.en) { toast.error('Le nom du modèle est requis'); return; }
    saveMutation.mutate({ ...tpl, updatedAt: new Date().toISOString() }, {
      onSuccess: () => { toast.success('Modèle enregistré'); navigate('/templates'); },
      onError: () => toast.error('Erreur lors de l\'enregistrement'),
    });
  };

  // Starter plan — block creation of custom templates (editing default ones is still blocked by save)
  if (isNew && tier === 'free' && !isInTrial) {
    return (
      <div className="flex flex-col items-center justify-center h-screen px-6 text-center space-y-6">
        <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center">
          <Lock size={28} className="text-primary" />
        </div>
        <div className="space-y-2 max-w-xs">
          <h2 className="text-xl font-bold font-display">
            {language === 'fr' ? 'Modèles personnalisés' : language === 'es' ? 'Plantillas personalizadas' : 'Custom templates'}
          </h2>
          <p className="text-sm text-muted-foreground leading-relaxed">
            {language === 'fr'
              ? 'Créez vos propres modèles de consentement avec l\'éditeur visuel. Disponible dès le plan Solo.'
              : language === 'es'
                ? 'Crea tus propias plantillas de consentimiento con el editor visual. Disponible desde el plan Solo.'
                : 'Create your own consent templates with the visual editor. Available from the Solo plan.'}
          </p>
        </div>
        <p className="text-xs text-primary font-medium">
          {language === 'fr' ? 'Disponible dès le plan Solo — 19€/mois' : language === 'es' ? 'Disponible desde el plan Solo — 19€/mes' : 'Available from the Solo plan — €19/month'}
        </p>
        <div className="flex gap-3">
          <button onClick={() => navigate('/templates')} className="px-5 py-2.5 bg-secondary text-secondary-foreground rounded-xl font-semibold text-sm">
            {language === 'fr' ? 'Retour' : language === 'es' ? 'Volver' : 'Back'}
          </button>
          <button onClick={() => navigate('/pricing')} className="flex items-center gap-2 px-5 py-2.5 bg-primary text-primary-foreground rounded-xl font-semibold text-sm">
            {language === 'fr' ? 'Voir les plans' : language === 'es' ? 'Ver planes' : 'View plans'}
          </button>
        </div>
      </div>
    );
  }

  const langFlag = language === 'fr' ? '🇫🇷' : language === 'en' ? '🇬🇧' : '🇪🇸';
  const cats = ['tattoo', 'microblading', 'piercing', 'aesthetic', 'medical'];
  const icons = ['📄', '🎨', '✨', '💎', '🌿', '💉', '✍️', '📋', '🏥', '💆'];

  return (
    <div className="h-screen flex flex-col overflow-hidden bg-background">
      {/* Top bar */}
      <div className="flex items-center gap-3 px-4 py-3 border-b border-border/50 bg-card/80 backdrop-blur shrink-0 z-10">
        <button onClick={() => navigate('/templates')} className="p-2 rounded-xl hover:bg-secondary transition-colors shrink-0"><ArrowLeft size={20} /></button>
        <input value={tpl.name[language]} onChange={e => upd({ name: { ...tpl.name, [language]: e.target.value } })} placeholder="Nom du modèle..." className="flex-1 text-base font-bold bg-transparent border-none outline-none text-foreground placeholder:text-muted-foreground/40 min-w-0" />
        <div className="flex items-center gap-2 shrink-0">
          {isTranslating && <Loader2 size={15} className="animate-spin text-primary" />}
          <span className="text-xs px-2.5 py-1.5 rounded-full bg-primary/10 text-primary font-medium">{langFlag} {language.toUpperCase()}</span>
          <button onClick={() => setShowPreview(!showPreview)} className={`p-2 rounded-xl transition-colors ${showPreview ? 'bg-primary/10 text-primary' : 'hover:bg-secondary text-muted-foreground'}`}>{showPreview ? <Eye size={17} /> : <EyeOff size={17} />}</button>
          <button onClick={handleSave} disabled={saveMutation.isPending} className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-xl font-semibold text-sm disabled:opacity-50 hover:bg-primary/90 transition-colors"><Save size={15} />{saveMutation.isPending ? 'Enregistrement...' : 'Enregistrer'}</button>
        </div>
      </div>

      <div className="flex flex-1 overflow-hidden">
        {/* Palette */}
        <div className="w-52 shrink-0 border-r border-border/50 bg-card/40 flex flex-col overflow-y-auto">
          <div className="p-3 space-y-1">
            {/* Sections prêtes */}
            <div className="mb-2">
              <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest px-2 py-1.5">Sections prêtes</p>
              <div className="space-y-0.5">
                {presets.map(preset => (
                  <div key={preset.id} className="group flex items-center gap-1 pr-1 rounded-xl hover:bg-secondary transition-colors">
                    <button
                      onClick={() => addPresetFromLibrary(preset)}
                      className="flex-1 flex items-center gap-2 px-2 py-2 text-left min-w-0"
                    >
                      <div className="w-6 h-6 rounded-lg flex items-center justify-center shrink-0 border bg-teal-500/10 text-teal-600 border-teal-200/50">
                        <Sparkles size={11} />
                      </div>
                      <span className="flex-1 text-xs font-semibold text-foreground truncate">{preset.name}</span>
                      <span className="text-[10px] text-muted-foreground bg-secondary px-1.5 py-0.5 rounded-full shrink-0">{preset.fields.length}</span>
                    </button>
                    <button
                      onClick={() => openEditPanel(preset)}
                      title="Modifier"
                      className="p-1 opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-primary transition-all rounded"
                    >
                      <Pencil size={11} />
                    </button>
                  </div>
                ))}
                <button
                  onClick={openCreatePanel}
                  className="w-full flex items-center gap-2 px-2 py-2 rounded-xl hover:bg-secondary transition-colors text-left text-xs text-muted-foreground font-medium"
                >
                  <div className="w-6 h-6 rounded-lg flex items-center justify-center shrink-0 border border-dashed border-border">
                    <Plus size={11} />
                  </div>
                  Nouvelle section
                </button>
              </div>
              <div className="flex items-center gap-2 mt-3 mb-1 px-2">
                <div className="h-px flex-1 bg-border/50" />
                <span className="text-[10px] text-muted-foreground uppercase tracking-widest">Champs individuels</span>
                <div className="h-px flex-1 bg-border/50" />
              </div>
            </div>

            {GROUPS.map(grp => {
              const GIcon = grp.icon;
              const isOpen = openGroup === grp.id;
              return (
                <div key={grp.id}>
                  <button onClick={() => setOpenGroup(isOpen ? null : grp.id)} className={`w-full flex items-center gap-2 px-2.5 py-2 rounded-xl text-xs font-medium transition-colors text-left ${isOpen ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:bg-secondary hover:text-foreground'}`}>
                    <GIcon size={13} className={grp.color} />
                    <span className="flex-1">{grp.label[language]}</span>
                    <ChevronDown size={11} className={`transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                  </button>
                  <AnimatePresence>
                    {isOpen && (
                      <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden pl-1">
                        <div className="py-1 space-y-0.5">
                          {grp.items.map(item => {
                            const Icon = item.icon;
                            return (
                              <button key={item.kind} onClick={() => addEl(item.kind)} className="w-full flex items-start gap-2 px-2 py-2 rounded-xl hover:bg-secondary transition-colors text-left">
                                <div className={`w-6 h-6 rounded-lg flex items-center justify-center shrink-0 border ${item.color}`}><Icon size={12} /></div>
                                <div className="min-w-0"><p className="text-xs font-semibold text-foreground">{item.label[language]}</p><p className="text-[10px] text-muted-foreground leading-tight">{item.desc[language]}</p></div>
                              </button>
                            );
                          })}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              );
            })}
          </div>
        </div>

        {/* Editor */}
        <div className="flex-1 flex flex-col overflow-hidden">
          <div className="flex-1 overflow-y-auto p-4 space-y-3">
            {/* Meta */}
            <div className="glass-card overflow-hidden">
              <button onClick={() => setShowInfo(!showInfo)} className="w-full px-4 py-3 flex items-center gap-2 text-left">
                <Settings2 size={14} className="text-muted-foreground" />
                <span className="text-sm font-semibold flex-1">Paramètres du modèle</span>
                <span className="text-lg">{tpl.icon}</span>
                <ChevronDown size={14} className={`transition-transform text-muted-foreground ${showInfo ? 'rotate-180' : ''}`} />
              </button>
              <AnimatePresence>
                {showInfo && (
                  <motion.div initial={{ height: 0 }} animate={{ height: 'auto' }} exit={{ height: 0 }} className="overflow-hidden">
                    <div className="px-4 pb-4 pt-2 border-t border-border/50 space-y-3">
                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1"><label className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide">Description</label><input value={tpl.description[language]} onChange={e => upd({ description: { ...tpl.description, [language]: e.target.value } })} placeholder="Description..." className="w-full p-2 bg-background border border-border rounded-xl text-sm focus:border-primary focus:outline-none" /></div>
                        <div className="space-y-1"><label className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide">Catégorie</label><select value={tpl.category} onChange={e => upd({ category: e.target.value })} className="w-full p-2 bg-background border border-border rounded-xl text-sm focus:border-primary focus:outline-none">{cats.map(c => <option key={c} value={c}>{t(`cat.${c}`)}</option>)}</select></div>
                      </div>
                      <div className="space-y-1"><label className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide">Icône</label><div className="flex gap-1.5 flex-wrap">{icons.map(ic => <button key={ic} onClick={() => upd({ icon: ic })} className={`w-8 h-8 text-base rounded-xl flex items-center justify-center border-2 transition-colors ${tpl.icon === ic ? 'border-primary bg-primary/10' : 'border-border hover:border-primary/30'}`}>{ic}</button>)}</div></div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Blocks */}
            {blocks.length === 0 ? (
              <div className="text-center py-16 text-muted-foreground"><div className="text-5xl mb-3">📝</div><p className="text-sm font-medium">Modèle vide</p><p className="text-xs mt-1">Utilisez la palette à gauche pour ajouter des éléments</p></div>
            ) : (
              <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                <SortableContext items={blocks.map(b => b.id)} strategy={verticalListSortingStrategy}>
                  <div className="space-y-2">
                    {blocks.map(block => (
                      <SortableBlock
                        key={block.id}
                        block={block}
                        expanded={expanded === block.id}
                        onToggle={() => setExpanded(expanded === block.id ? null : block.id)}
                        onRemove={() => removeBlock(block)}
                        onDuplicate={() => duplicateBlock(block)}
                        label={getLabel(block)}
                        fieldType={getFT(block)}
                      >
                        {renderContent(block)}
                      </SortableBlock>
                    ))}
                  </div>
                </SortableContext>
              </DndContext>
            )}
          </div>
        </div>

        {/* Preview */}
        <AnimatePresence>
          {showPreview && (
            <motion.div initial={{ width: 0, opacity: 0 }} animate={{ width: 290, opacity: 1 }} exit={{ width: 0, opacity: 0 }} className="shrink-0 border-l border-border/50 bg-card/30 flex flex-col overflow-hidden">
              <div className="px-4 py-3 border-b border-border/50 flex items-center gap-2 shrink-0">
                <Eye size={14} className="text-primary/60" />
                <span className="text-sm font-semibold">Aperçu client</span>
                <span className="ml-auto text-[10px] text-muted-foreground bg-secondary px-2 py-0.5 rounded-full">Temps réel</span>
              </div>
              <div className="flex-1 overflow-y-auto p-4"><Preview tpl={tpl} lang={language} /></div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Preset panel overlay */}
      <AnimatePresence>
        {panelOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 z-40 bg-black/40"
              onClick={closePanel}
            />
            <motion.div
              initial={{ x: '100%' }} animate={{ x: 0 }} exit={{ x: '100%' }}
              transition={{ type: 'spring', stiffness: 300, damping: 30 }}
              className="fixed right-0 top-0 h-full w-80 z-50 bg-card border-l border-border flex flex-col shadow-2xl"
            >
              {/* Header */}
              <div className="flex items-center gap-2 px-4 py-3 border-b border-border shrink-0">
                <span className="flex-1 text-sm font-bold">
                  {panelMode === 'create' ? 'Nouvelle section' : `Modifier "${editingPreset?.name}"`}
                </span>
                {panelMode === 'edit' && editingPreset && !editingPreset.is_system && (
                  <button
                    onClick={() => { setDeleteDialogPreset(editingPreset); closePanel(); }}
                    className="p-1.5 text-destructive hover:bg-destructive/10 rounded-lg transition-colors"
                    title="Supprimer"
                  >
                    <Trash2 size={14} />
                  </button>
                )}
                <button onClick={closePanel} className="p-1.5 text-muted-foreground hover:bg-secondary rounded-lg transition-colors">
                  <X size={16} />
                </button>
              </div>

              {/* Body */}
              <div className="flex-1 overflow-y-auto p-4 space-y-4">
                <div className="space-y-1.5">
                  <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Nom de la section *</label>
                  <input
                    value={panelName}
                    onChange={e => setPanelName(e.target.value)}
                    placeholder="Ex : Infos client"
                    className="w-full p-2.5 bg-background border-2 border-border rounded-xl text-sm focus:border-primary focus:outline-none"
                  />
                </div>

                <div className="space-y-2">
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Champs ({panelFields.length})</p>
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={e => {
                      const { active, over } = e;
                      if (!over || active.id === over.id) return;
                      const oldIdx = panelFields.findIndex(f => f.id === active.id);
                      const newIdx = panelFields.findIndex(f => f.id === over.id);
                      setPanelFields(p => arrayMove(p, oldIdx, newIdx));
                    }}
                  >
                    <SortableContext items={panelFields.map(f => f.id)} strategy={verticalListSortingStrategy}>
                      {panelFields.map(f => (
                        <PanelFieldRow
                          key={f.id}
                          field={f}
                          onLabelChange={val => updatePanelFieldLabel(f.id, val)}
                          onRemove={() => removePanelField(f.id)}
                        />
                      ))}
                    </SortableContext>
                  </DndContext>

                  {/* Add field button */}
                  <div className="relative">
                    <button
                      onClick={() => setPanelAddMenu(p => !p)}
                      className="flex items-center gap-1.5 text-xs text-primary font-medium px-3 py-1.5 rounded-lg border border-primary/20 hover:bg-primary/5 transition-colors"
                    >
                      <Plus size={12} /> Ajouter un champ
                    </button>
                    {panelAddMenu && (
                      <>
                        <div className="fixed inset-0 z-10" onClick={() => setPanelAddMenu(false)} />
                        <div className="absolute top-full left-0 mt-1 z-20 bg-card border border-border rounded-xl shadow-lg p-2 flex flex-wrap gap-1 min-w-[240px]">
                          {[
                            { kind: 'text', label: 'Texte court', icon: Type },
                            { kind: 'email', label: 'Email', icon: Mail },
                            { kind: 'tel', label: 'Téléphone', icon: Phone },
                            { kind: 'date', label: 'Date', icon: Calendar },
                            { kind: 'address', label: 'Adresse', icon: MapPin },
                            { kind: 'checkbox', label: 'Case à cocher', icon: CheckSquare },
                            { kind: 'signature', label: 'Signature', icon: PenTool },
                          ].map(({ kind, label, icon: Icon }) => (
                            <button
                              key={kind}
                              onClick={() => addPanelField(kind)}
                              className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg hover:bg-secondary text-xs font-medium transition-colors"
                            >
                              <Icon size={11} /> {label}
                            </button>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="flex gap-2 px-4 py-3 border-t border-border shrink-0">
                <button onClick={closePanel} className="flex-1 px-4 py-2 rounded-xl border border-border text-sm font-semibold hover:bg-secondary transition-colors">
                  Annuler
                </button>
                <button
                  onClick={savePanel}
                  disabled={!panelName.trim() || createPreset.isPending || updatePreset.isPending}
                  className="flex-1 px-4 py-2 rounded-xl bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 disabled:opacity-50 transition-colors"
                >
                  {createPreset.isPending || updatePreset.isPending ? 'Enregistrement...' : 'Enregistrer'}
                </button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Delete confirmation dialog */}
      <Dialog open={!!deleteDialogPreset} onOpenChange={open => { if (!open) setDeleteDialogPreset(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Supprimer &ldquo;{deleteDialogPreset?.name}&rdquo; ?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Cette section sera retirée de la bibliothèque mais les templates existants ne seront pas affectés.
          </p>
          <DialogFooter>
            <button onClick={() => setDeleteDialogPreset(null)} className="px-4 py-2 rounded-xl border border-border text-sm font-semibold hover:bg-secondary transition-colors">
              Annuler
            </button>
            <button
              onClick={async () => {
                if (deleteDialogPreset) {
                  await deletePreset.mutateAsync(deleteDialogPreset.id);
                  setDeleteDialogPreset(null);
                }
              }}
              disabled={deletePreset.isPending}
              className="px-4 py-2 rounded-xl bg-destructive text-destructive-foreground text-sm font-semibold hover:bg-destructive/90 disabled:opacity-50 transition-colors"
            >
              {deletePreset.isPending ? 'Suppression...' : 'Supprimer'}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ─── Panel field row (sortable) ───────────────────────────────────────────────

function PanelFieldRow({ field, onLabelChange, onRemove }: {
  field: SectionPresetField;
  onLabelChange: (v: string) => void;
  onRemove: () => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: field.id });
  const style = { transform: CSS.Transform.toString(transform), transition };
  const icons: Record<string, any> = { text: Type, email: Mail, tel: Phone, date: Calendar, textarea: AlignLeft, select: List, address: MapPin, signature: PenTool, photo: Camera, checkbox: CheckSquare };
  const Icon = icons[field.type] || Type;
  return (
    <div ref={setNodeRef} style={style} className={`flex items-center gap-2 p-2 bg-background border border-border rounded-xl ${isDragging ? 'opacity-40' : ''}`}>
      <div {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing p-1 text-muted-foreground touch-none shrink-0">
        <GripVertical size={13} />
      </div>
      <Icon size={12} className="text-muted-foreground shrink-0" />
      <input
        value={field.label.fr}
        onChange={e => onLabelChange(e.target.value)}
        placeholder="Label du champ"
        className="flex-1 text-xs bg-transparent border-none outline-none focus:ring-0 min-w-0"
      />
      <button onClick={onRemove} className="p-1 text-muted-foreground hover:text-destructive transition-colors shrink-0">
        <X size={12} />
      </button>
    </div>
  );
}

export default TemplateEditor;
